import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/layout/Header';
import PrimaryBox from '../../components/layout/PrimaryBox';
import CalendarModal from '../../components/modals/CalendarModal';
import ActivitiesTable from '../../components/tables/operations/supervision/ActivitiesTable';
import MachinesTable from '../../components/tables/operations/supervision/MachinesTable';
import RentalsTable from '../../components/tables/operations/supervision/RentalsTable';
import SitesTrucksLiveSupervision from '../../components/tables/operations/supervision/SitesTrucksLiveSupervision';
import SubContractorsTable from '../../components/tables/operations/supervision/SubContractorsTable';
import TempWorkersTable from '../../components/tables/operations/supervision/TempWorkersTable';
import ToolsTable from '../../components/tables/operations/supervision/ToolsTable';
import TrucksRidesTable from '../../components/tables/operations/supervision/TruckRidesTable';
import TrucksLiveSupervision from '../../components/tables/operations/supervision/TrucksLiveSupervision';
import WorkersTable from '../../components/tables/operations/supervision/WorkersTable';
import CustomTabs from '../../components/ui/CustomTabs';
import ToggleLine from '../../components/ui/ToggleLine';
import { localStorageKeys, stepModeEnum } from '../../constants';
import { useActivitiesQuery } from '../../hooks/queries/useActivitiesQuery';
import { useDailyTrucksRidesQuery } from '../../hooks/queries/useDailyTrucksRidesQuery';
import { useReportsQuery } from '../../hooks/queries/useReportsQuery';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { DailyTruckRide } from '../../services/apiSLTP/dailyReports/dailyTrucksRides';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getTomorrowDate } from '../../utils/dates';
import { getItemFromLocalStorage, storeItemToLocalStorage } from '../../utils/localStorage';

const SupervisionPage: React.FC = () => {
    const [activities, setActivities] = useState<DataItem[]>([]);
    const [dailyTrucksRides, setDailyTrucksRides] = useState<DailyTruckRide[]>([]);
    const [currentDate, setCurrentDate] = useState(new Date(getItemFromLocalStorage(localStorageKeys.DATE) || getTomorrowDate()));
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [selectedTab, setSelectedTab] = useState(parseInt(getItemFromLocalStorage(localStorageKeys.TAB.SUPERVISION) || '0'));
    const [onlyMySite, setOnlyMySite] = useState(getItemFromLocalStorage(localStorageKeys.SUPERVISIONMYSITES) === 'true');

    const [mode, setMode] = React.useState<number>(stepModeEnum.REPORT);

    const handleModeChange = (event: React.MouseEvent<HTMLElement>, newMode: number | null) => {
        if (newMode !== null) {
            setMode(newMode);
        }
    };

    const { user } = useAuth();
    const { t } = useTranslation();
    usePageTitle(capitalizeFirstLetter(t('supervision')));

    const { data, isLoading, isError, isSuccess, isRefetching, refetch } = useActivitiesQuery({
        queries: {
            startDate: currentDate.toISOString(),
            endDate: currentDate.toISOString(),
            stepMode: stepModeEnum.REPORT,
        },
    });

    const {
        data: dailyTrucksData,
        isLoading: isDailyTrucksLoading,
        isError: dailyTrucksIsError,
        isSuccess: dailyTrucksIsSuccess,
        isRefetching: dailyTrucksIsRefetching,
        refetch: dailyTrucksRefetch,
    } = useDailyTrucksRidesQuery({
        query: { date: currentDate.toISOString(), stepMode: stepModeEnum.REPORT },
    });

    const {
        data: dataReports,
        isLoading: isReportsLoading,
        refetch: refetchReports,
    } = useReportsQuery({ queries: { date: currentDate.toISOString() } });

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
        storeItemToLocalStorage(localStorageKeys.TAB.SUPERVISION, newValue.toString());
    };

    const handleDateChange = (newDate: string) => {
        setCurrentDate(new Date(newDate));
        storeItemToLocalStorage(localStorageKeys.DATE, newDate);
    };

    const handleMySiteFilterChange = (checked: boolean) => {
        setOnlyMySite(checked);
        storeItemToLocalStorage(localStorageKeys.SUPERVISIONMYSITES, checked.toString());
    };

    const filteredActivitiesForConducs = () => {
        if (!onlyMySite) return activities;
        else
            return activities.filter((activity) => {
                return activity.siteId.conducId?._id === user?._id && !activity.reportId.userId.isSpecial;
            });
    };

    useEffect(() => {
        if (data && isSuccess) {
            setActivities(data);
        }
    }, [data]);

    useEffect(() => {
        if (dailyTrucksData && dailyTrucksIsSuccess) {
            setDailyTrucksRides(dailyTrucksData);
        }
    }, [dailyTrucksData]);

    return (
        <PrimaryBox>
            <Header
                title={t('operations')}
                breadcrumbs={[{ label: capitalizeFirstLetter(t('supervision')) }]}
                subTitle={capitalizeFirstLetter(t('supervision'))}
                initialDate={currentDate}
                onDateChange={(newDate) => handleDateChange(newDate.toISOString())}
            >
                <ToggleLine title={capitalizeFirstLetter(t('mySites'))} checked={onlyMySite} onChange={handleMySiteFilterChange} />
                {/*<ToggleButtonGroup value={mode} exclusive onChange={handleModeChange}>
                    <ToggleButton value={stepModeEnum.REPORT}>{capitalizeFirstLetter(t('reports'))}</ToggleButton>
                    <ToggleButton value={stepModeEnum.VALIDATE}>{capitalizeFirstLetter(t('validation'))}</ToggleButton>
                </ToggleButtonGroup>*/}
            </Header>
            <CustomTabs
                selectedTab={selectedTab}
                handleTabChange={handleTabChange}
                tabLabels={[
                    t('reports'),
                    t('activities'),
                    t('machines'),
                    t('tools'),
                    t('rentals'),
                    t('tempWorkers'),
                    t('subContractors'),
                    t('trucksRides'),
                    t('liveRotationTracking'),
                    t('liveSitesRotations'),
                ]}
            />

            {selectedTab === 0 && (
                <WorkersTable
                    activities={filteredActivitiesForConducs()}
                    currentDate={currentDate}
                    dailyTrucksRides={dailyTrucksRides}
                    reports={dataReports || []}
                    stepMode={mode}
                />
            )}
            {selectedTab === 1 && (
                <>
                    <ActivitiesTable
                        activities={filteredActivitiesForConducs()}
                        currentDate={currentDate}
                        isLoading={isLoading}
                        dailyTrucksRides={dailyTrucksRides}
                        stepMode={mode}
                    />
                </>
            )}

            {selectedTab === 2 && <MachinesTable activities={filteredActivitiesForConducs()} currentDate={currentDate} stepMode={mode} />}
            {selectedTab === 3 && <ToolsTable activities={filteredActivitiesForConducs()} currentDate={currentDate} stepMode={mode} />}
            {selectedTab === 4 && <RentalsTable activities={filteredActivitiesForConducs()} currentDate={currentDate} stepMode={mode} />}
            {selectedTab === 5 && <TempWorkersTable activities={filteredActivitiesForConducs()} currentDate={currentDate} stepMode={mode} />}
            {selectedTab === 6 && <SubContractorsTable activities={filteredActivitiesForConducs()} currentDate={currentDate} stepMode={mode} />}

            {selectedTab === 7 && (
                <TrucksRidesTable
                    activities={filteredActivitiesForConducs()}
                    dailyTrucksRides={dailyTrucksRides}
                    currentDate={currentDate}
                    isLoading={isLoading || isDailyTrucksLoading}
                    stepMode={mode}
                />
            )}
            {selectedTab === 8 && <TrucksLiveSupervision date={currentDate} />}
            {selectedTab === 9 && <SitesTrucksLiveSupervision date={currentDate} onlyMySites={onlyMySite} />}

            {showCalendarModal && (
                <CalendarModal
                    currentDate={currentDate}
                    open={showCalendarModal}
                    handleClose={() => setShowCalendarModal(false)}
                    setCurrentDate={handleDateChange}
                />
            )}
        </PrimaryBox>
    );
};

export default SupervisionPage;
